/**
 * The site, node and enrolment routes of the route table (`routes.ts` spreads
 * these into `ROUTES`): PLAN §3's sites and nodes, D21's enrolment, D9's role
 * swap and D21's "Replace node".
 *
 * Cloud only, like the tenancy table: a node does not administer its own site.
 * Three kinds of caller (`RouteDef.auth`):
 *
 *   - a tenant admin (a Cognito user; `tenant.admin`, tenant-wide): the
 *     portal's site administration;
 *   - nobody (`auth: 'none'`): `POST /enrol`, gated by the one-time code it
 *     carries and rate-limited, because the node has no credential yet;
 *   - an enrolled node (`auth: 'node'`, a signed request —
 *     `@acp/cloud-link/nodeSignature.js`): `GET /node/self`.
 *
 * A path id from another tenant answers `not-found`, the same as one that does
 * not exist (D13).
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import type { RouteDef } from './routes.js';
import {
  createEnrolmentCodeRequestSchema,
  createSiteRequestSchema,
  enrolmentCodeResponseSchema,
  enrolRequestSchema,
  enrolResponseSchema,
  listNodesResponseSchema,
  listSitesResponseSchema,
  nodePathSchema,
  nodeSelfResponseSchema,
  replaceNodeRequestSchema,
  replaceNodeResponseSchema,
  sitePathSchema,
  siteResponseSchema,
  swapRolesRequestSchema,
  updateSiteRequestSchema,
} from './sites.js';

export const SITES_ROUTES = {
  createSite: {
    id: 'createSite',
    method: 'POST',
    path: '/sites',
    tag: 'sites',
    summary: 'Create a site',
    description: 'Creates an on-prem site: where a main, a backup and optionally a LAN witness node will run (PLAN §3, D21). Supplying `siteId` makes a retry idempotent: a site with that id is returned as it is. Every tenant also has the automatic cloud site `cloud` (§9.1), which is never created here.',
    params: null,
    query: null,
    body: createSiteRequestSchema,
    response: siteResponseSchema,
    successStatus: 201,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  listSites: {
    id: 'listSites',
    method: 'GET',
    path: '/sites',
    tag: 'sites',
    summary: 'List sites',
    description: 'Every site of the tenant — the automatic cloud site first — with the studios that name it.',
    params: null,
    query: null,
    body: null,
    response: listSitesResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  getSite: {
    id: 'getSite',
    method: 'GET',
    path: '/sites/{siteId}',
    tag: 'sites',
    summary: 'Get a site',
    description: 'One site, with the studios that name it.',
    params: sitePathSchema,
    query: null,
    body: null,
    response: siteResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  updateSite: {
    id: 'updateSite',
    method: 'PATCH',
    path: '/sites/{siteId}',
    tag: 'sites',
    summary: 'Rename a site',
    description: 'Changes a site’s name. Its id, kind and nodes are unchanged.',
    params: sitePathSchema,
    query: null,
    body: updateSiteRequestSchema,
    response: siteResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  deleteSite: {
    id: 'deleteSite',
    method: 'DELETE',
    path: '/sites/{siteId}',
    tag: 'sites',
    summary: 'Delete a site',
    description: 'Deletes an on-prem site and its revoked nodes’ records. `conflict` while it has an active node (replace or revoke them first) or a studio names it, and for the automatic cloud site.',
    params: sitePathSchema,
    query: null,
    body: null,
    response: siteResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  createEnrolmentCode: {
    id: 'createEnrolmentCode',
    method: 'POST',
    path: '/sites/{siteId}/enrolment-codes',
    tag: 'sites',
    summary: 'Issue an enrolment code',
    description:
      'A one-time code (at most 24 h) and the `docker run … -e ACP_ENROL=<code> …` line that installs a node with it (D21). The code encodes this regional stack’s URL; the cloud keeps only a hash of its secret, so it is shown once. The node enrolling with it becomes main, backup or — with `--role witness` — witness, in that order. Not for the cloud site, whose nodes the cloud-production launcher enrols (phase 6): `validation-failed`.',
    params: sitePathSchema,
    query: null,
    body: createEnrolmentCodeRequestSchema,
    response: enrolmentCodeResponseSchema,
    successStatus: 201,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  listNodes: {
    id: 'listNodes',
    method: 'GET',
    path: '/sites/{siteId}/nodes',
    tag: 'sites',
    summary: 'List a site’s nodes',
    description: 'Every node of the site, revoked ones included, with its role, link state, last contact, packages, certificate expiry and last health report (PLAN §10.2, §11.1).',
    params: sitePathSchema,
    query: null,
    body: null,
    response: listNodesResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  replaceNode: {
    id: 'replaceNode',
    method: 'POST',
    path: '/sites/{siteId}/nodes/{nodeId}/replace',
    tag: 'sites',
    summary: 'Replace a node',
    description:
      'D21’s “Replace node”: revokes the node — its key is refused from now on, its site link is closed and its DNS name withdrawn — and issues a one-time code for its replacement, which takes over its role. Replacing an already revoked node issues a fresh code.',
    params: nodePathSchema,
    query: null,
    body: replaceNodeRequestSchema,
    response: replaceNodeResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  swapRoles: {
    id: 'swapRoles',
    method: 'POST',
    path: '/sites/{siteId}/swap-roles',
    tag: 'sites',
    summary: 'Swap main and backup',
    description:
      'Makes `mainNodeId` the site’s main node and the current main its backup (D21: “roles can be swapped in the portal”). Both nodes are told over the site link. Idempotent: naming the node that is already main changes nothing. Per-production active/standby roles are unaffected (§8.1). `conflict` unless the site has both an active main and an active backup.',
    params: sitePathSchema,
    query: null,
    body: swapRolesRequestSchema,
    response: listNodesResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  enrol: {
    id: 'enrol',
    method: 'POST',
    path: '/enrol',
    tag: 'node',
    summary: 'Enrol a node (exchange an enrolment code)',
    description:
      'The node end of D21: exchanges a one-time enrolment code for a node identity. Unauthenticated — the code is the credential — and rate-limited per source address; a code is refused after its first use, after it expires, or after five wrong secrets (`unauthorized` for all three, so a guess learns nothing). The node registers its Ed25519 public key (its requests are signed with it from now on) and an X25519 key the cloud seals deliveries to. The first node of a site becomes main, the second backup; a third is refused (`conflict`) unless it enrols as `witness`. The answer carries the stack’s URLs, what the node needs to verify Cognito tokens offline, and the site’s signing key (D20); its local HTTPS certificate follows over the site link (D16).',
    params: null,
    query: null,
    body: enrolRequestSchema,
    response: enrolResponseSchema,
    successStatus: 201,
    permission: null,
    permissionScope: 'tenant',
    auth: 'none',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  getNodeSelf: {
    id: 'getNodeSelf',
    method: 'GET',
    path: '/node/self',
    tag: 'node',
    summary: 'The calling node',
    description: 'The node the request is signed by, as the cloud has it. The simplest signed call: a janitor checks its credentials with it, and a revoked node is refused (`unauthorized`).',
    params: null,
    query: null,
    body: null,
    response: nodeSelfResponseSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    auth: 'node',
    errors: [],
  },
} as const satisfies Record<string, RouteDef>;
